import { Injectable } from '@angular/core';
import { ICustomersController } from '@utils/interfaces/controller';
import { HttpClient } from '@angular/common/http';
import { CustomerReniecResponse, ICompanyResponse, ICustomerResponse } from '@utils/interfaces/response';
import { catchError, interval, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { environment } from 'environments/environment';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { IKbaQuestionsResponse } from '@utils/interfaces/response/kba-questions-response.interface';
import { IKbaStatusResponse } from '@utils/interfaces/response/kba-status-response.interface';
import { KbaAnswers } from '@utils/types/questions.type';
import { AuthService } from '@modules/business-accounts/services/auth/auth.service';
import { ILegalRepresentativeResponse } from '@utils/interfaces/response/legal-representative.interface';
import { IAccountsCounterResponse } from '@utils/interfaces/response/accounts-counter-response.interface';
import { ENABLE_LIMIT_ACCOUNTS } from '@modules/business-accounts/utils/constants/user-workflow-constants';
import { SessionService } from '@domain/application/observable-services/session.service';
import { MonitorService } from '../monitor/monitor.service';

@Injectable({
  providedIn: 'root',
})
export class CustomersApiService implements ICustomersController {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly sessionService: SessionService,
    private readonly monitorService: MonitorService
  ) {}

  answerKbaQuestions(kbaId: string, questions: KbaAnswers): Observable<IKbaStatusResponse> {
    return this.http.post<IKbaStatusResponse>(
      `${environment.bffKbaId}/${kbaId}`,
      {
        questions,
      },
      {
        headers: this.authService.buildHeaders(),
      }
    );
  }

  fetchKbaQuestions(documentNumber: string, documentType: DocumentType): Observable<IKbaQuestionsResponse> {
    return this.http.get<IKbaQuestionsResponse>(`${environment.bffKba}`, {
      params: {
        documentNumber,
        documentType,
      },
      headers: this.authService.buildHeaders(),
    });
  }

  fetchBlackList(documentNumber: string, documentType: DocumentType): Observable<boolean> {
    return this.http
      .get(`${environment.bffCustomersBlackList}`, {
        params: { documentNumber, documentType },
        headers: this.authService.buildHeaders(),
      })
      .pipe(map((response) => Boolean(response)));
  }

  fetchAccountsCounter(): Observable<IAccountsCounterResponse> {
    if (!ENABLE_LIMIT_ACCOUNTS) return of({ businessAccounts: 0 });

    return this.http
      .get<IAccountsCounterResponse>(`${environment.bffAccountsCounter}`, {
        headers: this.authService.buildHeaders(),
      })
      .pipe(map(({ businessAccounts = 0 }) => ({ businessAccounts: Number(businessAccounts) })));
  }

  fetchCustomer(
    documentNumber: string,
    documentType: Exclude<DocumentType, DocumentType.RUC>
  ): Observable<ICustomerResponse> {
    return this.authService.waitForHeadersAvailability(() =>
      this.http.get<ICustomerResponse>(`${environment.bffCustomers}`, {
        params: {
          documentNumber,
          documentType,
        },
        headers: this.authService.buildHeaders(),
      })
    );
  }

  fetchReniecData(documentNumber: string) {
    return this.authService.waitForHeadersAvailability(() =>
      this.http.post<CustomerReniecResponse>(
        `${environment.bffCustomersInfo}`,
        {
          identityDocument: {
            type: 'DNI',
            number: documentNumber,
          },
        },
        { headers: this.authService.buildHeaders() }
      )
    );
  }

  fetchCompany(ruc: string): Observable<ICompanyResponse> {
    return this.authService.waitForHeadersAvailability(() =>
      this.http.get<ICompanyResponse>(`${environment.bffCustomersCompanies}`, {
        params: {
          documentNumber: ruc,
          documentType: DocumentType.RUC,
        },
        headers: this.authService.buildHeaders(),
      })
    );
  }

  fetchLegalRepresentative(ruc: string, legalRepresentantNumber: string) {
    return this.authService
      .waitForHeadersAvailability(() =>
        this.http.get<ILegalRepresentativeResponse>(
          `${environment.bffCustomersCompaniesLegalRepresentative}/${ruc}/legal-representative`,
          {
            params: {
              documentNumber: legalRepresentantNumber,
              documentType: DocumentType.DNI,
            },
            headers: this.authService.buildHeaders(),
          }
        )
      )
      .pipe(
        switchMap(({ legalRepresentative }: ILegalRepresentativeResponse) => {
          return this.fetchReniecData(legalRepresentative.identityDocumentNumber);
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  createBussinessAccount(data: any) {
    const { codeOtp, signatureId } = this.sessionService.getCommonInfoValue;
    const headers = {
      'X-OTP': codeOtp,
      'X-OTP-Signature-Id': signatureId,
      'X-Monitor-Tracking-Device': this.monitorService.monitorData
        ? btoa(JSON.stringify(this.monitorService.monitorData))
        : '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',
      'X-Monitor-Tracking-Data': btoa(
        JSON.stringify({
          ip: this.monitorService.geoIp?.query ? this.monitorService.geoIp?.query : '192.168.1.1',
          originId: 'WEB-JS',
          securityLevel: '0P',
          sessionId: this.authService.authHeaders?.sessionToken,
        })
      ),
    };

    return this.http.post(`${environment.bffCreate}`, data, {
      headers: this.authService.buildHeaders(headers),
    });
  }

  getCorrelationStatus(correlationId: string) {
    return interval(5000)
      .pipe(
        mergeMap(() =>
          this.http.get(`${environment.bffCorrelationsStatus}/${correlationId}/status`, {
            headers: this.authService.buildHeaders(),
          })
        )
      );
  }
}
